import React, { lazy, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from '@bbkShared/react-router';
import { SuspenseLoader } from '@bbkAdminComponents/loaders/suspense-loader';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import {
  selectApp,
  selectAppState,
  selectCompany,
  selectUser,
} from '@bbkAdminRedux/app/selectors';
import { MainContent } from '@bbkAdminComponents/app-layout/main-content';
import { AuthorizedRouter } from '@bbkAdminRouter/authorized.router';
import { TrialSignupPage3 } from '@bbkAdminComponents/trial-signup/TrialSignupPage3';
import {
  invitationSlice,
  useSelectInvitesByUserQuery,
} from '@bbkAdminRedux/rtkq/invitation.slice';
import { SignupPaths } from '@bbkAdminRouter/signup-paths';
import { LogoutPage } from '@bbkAdminRouter/logout.page';

const Selector = lazy(() => import('@bbkAdminComponents/selector'));
const InternalRouter = lazy(
  () => import('@bbkAdminComponents/internal/internal-router')
);

export const LoggedInRouter: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const app = useSelector(selectApp);
  const invites = useSelectInvitesByUserQuery();
  const { isFetching: isFetchingInvites } =
    invitationSlice.endpoints.getPendingInvitationsByEmail.useQuery();
  const suppressSelectorPage = BrightbackSession.getSuppressSelectorPage();

  const loadedNecessaryData = useSelector(selectAppState).loadedNecessaryData;

  useEffect(() => {
    const pathname = location.pathname;
    const adminAppPagesRegex =
      /^\/(trial_signup|company|internal|logout|cbidp-exchange)/;
    if (!adminAppPagesRegex.test(pathname)) {
      history.push(LogInPaths.company);
    }
  }, [history, location.pathname]);

  return (
    <Suspense
      fallback={
        <MainContent>
          <SuspenseLoader message=" " />
        </MainContent>
      }
    >
      <Switch>
        {/* Still trying to set all items, wait until finished to choose what to show */}
        {(!loadedNecessaryData || isFetchingInvites) && (
          <Route>
            <SuspenseLoader message=" " />
          </Route>
        )}

        <Route path={SignupPaths.trialSignup3}>
          <TrialSignupPage3 />
        </Route>

        <Route path={LogInPaths.mfaReset}>
          <LogoutPage />
        </Route>

        <Route path={LogInPaths.logout}>
          <LogoutPage />
        </Route>

        <Route path={LogInPaths.selector} exact>
          <MainContent>
            <Selector />
          </MainContent>
        </Route>

        {/* If a user has pending invites and has not previously suppressed this page */}
        {invites.length > 0 && !suppressSelectorPage && (
          <Redirect to={LogInPaths.selector} />
        )}

        {/** No company or app set yet, but there is a user with companies. Need to go to the selector. */}
        {!(company && app) && user && user.companies.length > 1 && (
          <Switch>
            <Route
              path={LogInPaths.signIn}
              exact
              render={() => <Redirect to={LogInPaths.selector} />}
            />
            <Route
              path="/company"
              exact
              render={() => <Redirect to={LogInPaths.selector} />}
            />
          </Switch>
        )}

        {/* If a logged-in user has no companies associated with them, default to the No Companies page */}
        {user && user.companies.length === 0 && (
          <Redirect to={LogInPaths.selector} />
        )}

        <Route path="/internal/">
          <MainContent>
            <InternalRouter />
          </MainContent>
        </Route>

        <Route>
          <AuthorizedRouter />
        </Route>
      </Switch>
    </Suspense>
  );
};
